import { classNames } from '@commenda/utils';
import { Table, flexRender } from '@tanstack/react-table';
import React from 'react';
import { Checkbox } from '../../Checkbox';
import { TableRow as TRow } from '../../Table';
import { ColumnGroup, ColumnResizeControl, CustomColumnDef } from '../types';
import {
  FLEX_SPACER_HEADER_CLASSNAME,
  FLEX_SPACER_STYLE,
  getAlignClass,
  getColumnId,
  getColumnWidthStyle,
  getStickyColumnClasses,
  getStickyPositionLive,
  getStickyPositionVar,
} from '../utils';
import { HeaderCell } from './HeaderCell';

export function TableHeader<TData, TValue>({
  table,
  groups: finalColumnGroups,
  tableHeadClassName = '',
  onColumnReorder: _onColumnReorder,
  onDragPreview: _onDragPreview,
  columnResizeConfig,
  columnResizeControls,
  rowSelectionControl,
}: {
  table: Table<TData>;
  groups: ColumnGroup<TData, TValue>[];
  tableHeadClassName?: string;
  onColumnReorder?: (
    groupId: string,
    sourceColumnId: string,
    targetColumnId: string,
    position: 'before' | 'after'
  ) => void;
  onDragPreview?: (leftPx: number | null) => void;
  columnResizeConfig?: { isEnabled: boolean };
  /** Per-column resize controls (keyed by column id); drives the header handle. */
  columnResizeControls?: Record<string, ColumnResizeControl>;
  rowSelectionControl?: {
    isEnabled: boolean;
    isRowSelected: (row: TData) => boolean;
    toggleRowById: (id: string) => void;
    toggleAll: (subset?: TData[]) => void;
    getRowId: (row: TData) => string;
  };
}) {
  // Anchor the spacer before the first VISIBLE sticky-right header (by id, not a
  // fixed index — a hidden leading column must not drop it); trailing when none.
  const rightStickyGroupId = finalColumnGroups.find(
    (g) => g.sticky === 'right'
  )?.id;
  const firstRightStickyHeaderId = rightStickyGroupId
    ? table
        .getHeaderGroups()
        .flatMap((hg) => hg.headers)
        .find(
          (h) =>
            (h.column.columnDef as CustomColumnDef<TData, TValue>).meta
              ?.groupId === rightStickyGroupId
        )?.column.id
    : undefined;
  const showSpacer = !!columnResizeConfig?.isEnabled;

  return (
    <TRow>
      {table.getHeaderGroups().map((headerGroup) => (
        <React.Fragment key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const columnDef = header.column.columnDef as CustomColumnDef<
              TData,
              TValue
            >;

            const originalGroup = finalColumnGroups.find(
              (group) => group.id === columnDef.meta?.groupId
            );

            if (!originalGroup) return null;

            const widthStyle = getColumnWidthStyle(
              columnDef.width,
              columnResizeConfig?.isEnabled,
              header.getSize(),
              header.column.id,
              // Resize path: lock every column so the spacer absorbs the slack.
              columnResizeConfig?.isEnabled || !!originalGroup.sticky
            );

            const groupColumnIndex = originalGroup.columns.findIndex(
              (col) => getColumnId(col) === header.column.id
            );

            // Check if this is the last visible column
            const allVisibleColumns = finalColumnGroups
              .flatMap((g) => g.columns)
              .filter((c) => table.getColumn(getColumnId(c))?.getIsVisible?.());
            const isLastVisibleHeader =
              allVisibleColumns.length > 0 &&
              getColumnId(allVisibleColumns[allVisibleColumns.length - 1]) ===
                header.column.id;

            const firstVisibleHeaderCol = finalColumnGroups
              .flatMap((g) => g.columns)
              .find((c) => table.getColumn(getColumnId(c))?.getIsVisible?.());
            const isFirstVisibleHeader =
              !!firstVisibleHeaderCol &&
              getColumnId(firstVisibleHeaderCol) === header.column.id;

            // Determine if column is in a sticky group (pinned by developer)
            const isPinned = !!originalGroup.sticky;

            // For sticky groups, determine if this is the edge column (for separator shadow)
            const visibleGroupHeaders = finalColumnGroups
              .find((g) => g.id === originalGroup.id)
              ?.columns.filter((c) =>
                table.getColumn(getColumnId(c))?.getIsVisible?.()
              );
            const isFirstInGroup =
              visibleGroupHeaders?.[0] &&
              getColumnId(visibleGroupHeaders[0]) === header.column.id;
            const isLastInGroup =
              visibleGroupHeaders?.[visibleGroupHeaders.length - 1] &&
              getColumnId(
                visibleGroupHeaders[visibleGroupHeaders.length - 1]
              ) === header.column.id;

            // Present only for resizable columns (opted out via enableResizing:
            // false). Carries the drag handler + shared hover/active state.
            const resizeControl = columnResizeControls?.[header.column.id];

            const headerCell = (
              <HeaderCell
                className={classNames(
                  'bg-bg-primary relative',
                  // Non-sticky columns: no vertical borders
                  '',
                  getStickyColumnClasses(originalGroup, { isHeader: true }),
                  tableHeadClassName
                )}
                style={{
                  ...widthStyle,
                  ...(originalGroup.sticky === 'left' && {
                    left: columnResizeConfig?.isEnabled
                      ? getStickyPositionVar(table, finalColumnGroups, originalGroup, groupColumnIndex)
                      : `${getStickyPositionLive(table, finalColumnGroups, originalGroup, groupColumnIndex)}px`,
                  }),
                  ...(originalGroup.sticky === 'right' && {
                    right: columnResizeConfig?.isEnabled
                      ? getStickyPositionVar(table, finalColumnGroups, originalGroup, groupColumnIndex)
                      : `${getStickyPositionLive(table, finalColumnGroups, originalGroup, groupColumnIndex)}px`,
                  }),
                }}
                sort={
                  header.column.getCanSort()
                    ? {
                        direction: header.column.getIsSorted() as
                          | 'asc'
                          | 'desc'
                          | null,
                        onToggle:
                          header.column.getToggleSortingHandler() as unknown as (
                            e: unknown
                          ) => void,
                      }
                    : undefined
                }
                columnOptions={undefined}
                resizeControl={resizeControl}
              >
                {!header.isPlaceholder ? (
                  <div
                    className={classNames(
                      'flex items-center gap-2 min-w-0',
                      getAlignClass(columnDef.align),
                      columnDef.headerClassName || ''
                    )}
                  >
                    {isFirstVisibleHeader && rowSelectionControl?.isEnabled
                      ? (() => {
                          const subset = table
                            .getRowModel()
                            .rows.map(
                              (r) => r.original
                            ) as unknown[] as TData[];
                          const total = subset.length;
                          const selectedCount = subset.filter((r) =>
                            rowSelectionControl.isRowSelected(r)
                          ).length;
                          const all = total > 0 && selectedCount === total;
                          const some =
                            selectedCount > 0 && selectedCount < total;
                          return (
                            <div className="checkbox-cell shrink-0">
                              <Checkbox
                                checked={all}
                                indeterminate={some}
                                aria-label="Select all"
                                onCheckedChange={() =>
                                  rowSelectionControl.toggleAll?.(subset)
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          );
                        })()
                      : null}
                    <div
                      data-dt-col={header.column.id}
                      className={classNames('min-w-0 flex-1', columnResizeConfig?.isEnabled ? 'truncate' : '')}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                  </div>
                ) : null}
              </HeaderCell>
            );
            return (
              <React.Fragment key={header.id}>
                {showSpacer &&
                  header.column.id === firstRightStickyHeaderId && (
                    <th
                      aria-hidden
                      className={FLEX_SPACER_HEADER_CLASSNAME}
                      style={FLEX_SPACER_STYLE}
                    />
                  )}
                {headerCell}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      ))}
      {/* Trailing spacer when no visible sticky-right header anchors the seam. */}
      {showSpacer && !firstRightStickyHeaderId && (
        <th
          aria-hidden
          className={FLEX_SPACER_HEADER_CLASSNAME}
          style={FLEX_SPACER_STYLE}
        />
      )}
    </TRow>
  );
}
